<template>
    <div>
        <FormSection title="Payer Information" :full-height="false" :application-id="application">
            <template #content>
                <div class="row p-2 pb-0">
                    <div class="col-12 offset-md-1 col-md-10 row g-0">
                        <div class="col-md-6 col-12 pe-3">
                            <div
                                class="form-group col-12"
                                v-if="
                                    !patientDetails.payerInformation.medicareNumberNA &&
                                    !!patientDetails.payerInformation.medicareNumber
                                "
                            >
                                <label id="Patient_Medicare_Number_Label" for="medicare-number">
                                    <span class="badge me-1">A0600B</span> Medicare Number
                                </label>
                                <p>{{ patientDetails.payerInformation.medicareNumber }}</p>
                            </div>
                            <div class="form-group col-12 ps-0">
                                <label for="mbi-number" id="Mbi_Number_Label">
                                    Medicare Beneficiary Identifier(MBI)
                                    <span
                                        v-if="!patientDetails.payerInformation.medicareBeneficiaryIdentificationNA"
                                        class="text-danger"
                                        >*</span
                                    >
                                    <a
                                        tabindex="0"
                                        href="javascript:void(0);"
                                        role="button"
                                        v-custom-popover="{ content: sanitizeMarkup(mbiTooltip) }"
                                    >
                                        <i class="fas fa-info-circle text-primary font-size-xs"></i>
                                    </a>
                                </label>
                                <input
                                    class="form-control"
                                    maxlength="50"
                                    name="mbi-number"
                                    type="text"
                                    placeholder="Enter Medicare Beneficiary Identifier (MBI)"
                                    :value="
                                        formatToUppercase(
                                            patientDetails.payerInformation.medicareBeneficiaryIdentification
                                        )
                                    "
                                    :disabled="patientDetails.payerInformation.medicareBeneficiaryIdentificationNA"
                                    @change="(e) => handleMedicareNumber(e)"
                                    @blur="v$.patientDetails.payerInformation.medicareBeneficiaryIdentification.$touch"
                                    :class="`${
                                        v$.patientDetails.payerInformation.medicareBeneficiaryIdentification.$error
                                            ? 'field-invalid'
                                            : ''
                                    }`"
                                />
                            </div>
                            <div class="form-group row">
                                <div class="col-12">
                                    <div class="form-check custom-checkbox form-check-inline mt-2">
                                        <input
                                            type="checkbox"
                                            id="referral-medicare-beneficiary-identification-na"
                                            class="form-check-input au-target"
                                            v-model="
                                                patientDetails.payerInformation.medicareBeneficiaryIdentificationNA
                                            "
                                        />
                                        <label
                                            class="form-check-label"
                                            for="referral-medicare-beneficiary-identification-na"
                                        >
                                            <span class="me-1"
                                                >Not Applicable
                                                <a
                                                    tabindex="0"
                                                    href="javascript:void(0);"
                                                    role="button"
                                                    v-custom-popover="{
                                                        content: `If an MBI is entered above, selecting Not Applicable will erase it.`
                                                    }"
                                                >
                                                    <i class="fas fa-info-circle text-primary font-size-xs"></i>
                                                </a>
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <span
                                v-if="v$.patientDetails.payerInformation.medicareBeneficiaryIdentification.$error"
                                class="field-invalid"
                                >Medicare Beneficiary Identifier is required unless N/A is checked.</span
                            >
                        </div>
                        <div class="form-group col-md-6 col-12">
                            <label for="medicaid-number"> Medicaid Number </label>
                            <input
                                class="form-control"
                                maxlength="50"
                                placeholder="Enter Medicaid Number"
                                name="medicaid-number"
                                type="text"
                                v-model="patientDetails.payerInformation.medicaidNumber"
                                :disabled="
                                    otherMedicaid == otherMedicaidEnum?.MedicaidPending ||
                                    otherMedicaid == otherMedicaidEnum?.NotAMedicaid
                                "
                            />
                            <div class="form-group row ms-1">
                                <div class="form-check custom-radio form-check-inline mt-2">
                                    <input
                                        type="radio"
                                        id="referral-medicaid-pending"
                                        name="other-medicaid"
                                        class="form-check-input au-target"
                                        :value="otherMedicaidEnum.MedicaidPending"
                                        :checked="otherMedicaid === otherMedicaidEnum.MedicaidPending"
                                        @click="otherMedicaidRadioClick(otherMedicaidEnum.MedicaidPending)"
                                    />
                                    <label class="form-check-label" for="referral-medicaid-pending">
                                        <span class="me-1">Pending</span>
                                    </label>
                                </div>
                            </div>
                            <div class="form-group row ms-1">
                                <div class="form-check custom-radio form-check-inline">
                                    <input
                                        type="radio"
                                        id="referral-no-medicaid"
                                        name="other-medicaid"
                                        class="form-check-input au-target"
                                        :value="otherMedicaidEnum?.NotAMedicaid"
                                        :checked="otherMedicaid === otherMedicaidEnum?.NotAMedicaid"
                                        @click="otherMedicaidRadioClick(otherMedicaidEnum?.NotAMedicaid)"
                                    />
                                    <label class="form-check-label" for="referral-no-medicaid">
                                        <span class="me-1">Not a Medicaid Recipient</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="form-group col-12">
                            <i class="fas fa-question-circle"></i>
                            <a
                                href="https://www.cms.gov/Medicare/New-Medicare-Card/Providers/Providers-and-office-managers.html"
                                target="_blank"
                            >
                                Click here to review CMS guidelines on the New Medicare Beneficiary Identifier
                            </a>
                        </div>
                    </div>
                    <div class="col-12 col-md-10 offset-md-1 ps-0">
                        <EligibilityCheck
                            :first-name="patientOptions.data.firstName"
                            :last-name="patientOptions.data.lastName"
                            :gender="patientOptions.data.gender"
                            :location-id="patientOptions.data.branchId"
                            :date-of-birth="patientOptions.data.dateOfBirth"
                            :medicare-number="patientDetails.payerInformation.medicareNumber"
                            :missing-url="eligibilityMissingUrl"
                            :mbi-number="patientDetails.payerInformation.medicareBeneficiaryIdentification"
                            :medicare-number-na="patientDetails.payerInformation.medicareNumberNA"
                            :mbi-number-na="patientDetails.payerInformation.medicareBeneficiaryIdentificationNA"
                            :application="application"
                            :account-id="accountId"
                        />
                    </div>
                    <div class="col-12 col-md-10 offset-md-1 row g-0">
                        <PayerTable
                            :add-new-payer="addNewPayer"
                            :get-current-patient-payers="getCurrentPatientPayers"
                            :get-patient-payers="getPatientPayers"
                            :update-patient-payer="updatePatientPayer"
                            :delete-patient-payer="deletePatientPayer"
                        />
                    </div>
                </div>
            </template>
        </FormSection>
    </div>
</template>

<script lang="ts" src="./PayerInformation.ts"></script>
